import React, {FC, useState, useRef} from "react"
import PropTypes from "prop-types"

import History from "@irati/utils/history"
import theme from "@irati/ui/src/global/theme"
import Flex from "@irati/ui/src/Flex"
import {H6, Label} from "@irati/ui/src/Typography"
import FormattedMessage, {injectIntl, InjectIntl} from "@irati/utils/lang"
import Button from "@irati/ui/src/Button"
import Slider from "@irati/ui/src/Slider"
import {ButtonLink, ButtonRouterLink} from "@irati/ui/src/Link"
import adminTracking from "@irati-tracking/admin"

import RadialSteps from "../../RadialSteps"
import {SliderContainer} from "./styled"
import {Action, ONBOARDING_NEXT_EVENT} from "./OnboardingSteps"
import OnboardingReasonModal from "./OnboardingReasonsModal"
import {ADD_PEOPLE_MULTIPLE} from "../../../routes/routeConfig"

const SLIDER_SETTINGS = {
  arrows: false,
  dots: false,
  speed: 800,
  slidesToShow: 1,
  slidesToScroll: 1,
  autoplay: false,
  infinite: true,
  pauseOnHover: false,
  adaptiveHeight: false,
  variableWidth: false
}

interface Props extends InjectIntl {
  title: string,
  event: string,
  slides: Action[],
  completed: number,
  total: number,
  onCallback: () => void
}

const OnboardingSlider: FC<Props> = ({title, event, slides, completed, total, onCallback, formatMessage}, {apps: {HOME, ADMIN}}) => {
  const [currentSlide, setCurrentSlide] = useState<number>(0)
  const [modal, setModal] = useState<boolean>(false)

  const btnText = slides[currentSlide].button
  const sliderRef = useRef<Slider>(null)

  const handleNext = (element: any) => {
    if (currentSlide + 1 < slides.length) {
      adminTracking.adminDashboardOnboardingV2Action(event, ONBOARDING_NEXT_EVENT)
      setCurrentSlide(currentSlide + 1)
      return element.current?.slickNext()
    }
  }

  const handleActionEvent = () => {
    adminTracking.adminDashboardOnboardingV2Action(event, slides[currentSlide].event)
  }

  const handleSlideLink = () => {
    History.push(ADD_PEOPLE_MULTIPLE, ADMIN)
  }

  return (
    <>
      <SliderContainer direction="column" justifyContent="space-between" height="100%">
        <div>
          <Flex justifyContent="space-between" marginBottom={.5}>
            <div>
              <Button small cta onClick={onCallback}>
                <FormattedMessage id={title} />
              </Button>
            </div>
            <RadialSteps completed={completed} steps={total} fill={theme.colors.greys.lightest} extraSmall hideLabel />
          </Flex>
          <Slider ref={sliderRef} settings={SLIDER_SETTINGS}>
            {slides.map((slide: Action) => {
              const slideTitle = slide.title
              const slideSubTitle = slide.subTitle
              const slideLink = slide.subTitleLink
              return (
                <Flex direction="column">
                  <H6>
                    <FormattedMessage id={slideTitle} />
                  </H6>
                  <Label>
                    {slideLink ? <FormattedMessage
                      id={slideSubTitle}
                      values={{
                        link: <Button small cta onClick={handleSlideLink}>
                          <FormattedMessage id={slideLink} />
                        </Button>
                      }} />
                      : <FormattedMessage id={slideSubTitle} />}
                  </Label>
                  {slide.reasons && <Flex justifyContent="flex-start">
                    <Button marginTop={2} small cta onClick={() => setModal(true)}>
                      <FormattedMessage id="admin.components.onboardingProgress.setup.reasonsBtn" />
                    </Button>
                  </Flex>}
                </Flex>
              )
            })}
          </Slider>
        </div>
        <Flex justifyContent={currentSlide < slides.length - 1 ? "space-between" : "flex-end"}>
          {currentSlide < slides.length - 1 &&
          <div>
            <Button small cta onClick={() => handleNext(sliderRef?.current?.slickSlider)}>
              <FormattedMessage id="platform.tour.buttons.next" />
            </Button>
          </div>
          }
          {slides[currentSlide].route ?
            <ButtonRouterLink
              small
              primary
              onClick={handleActionEvent}
              to={slides[currentSlide].route}
              app={slides[currentSlide].external ? HOME : ADMIN}
              marginRight>
              <FormattedMessage id={btnText} />
            </ButtonRouterLink>
            :
            <ButtonLink
              small
              primary
              onClick={handleActionEvent}
              href={formatMessage({id: slides[currentSlide].link})}
              target="_blank">
              <FormattedMessage id={btnText} />
            </ButtonLink>
          }
        </Flex>
        <OnboardingReasonModal
          show={modal}
          reasons={slides[currentSlide].reasons || []}
          sourceEvent={event}
          onHide={() => setModal(false)} />
      </SliderContainer>
    </>
  )
}

OnboardingSlider.contextTypes = {
  apps: PropTypes.object.isRequired
}

export default injectIntl(OnboardingSlider)
